import type { ThemeProps } from "./theme";

// How a listing captions its valuation figure, which number it shows, and
// whether the detail-page info card lists it at all.
//
// MIRRORED — canonical vocabulary is tva-api/constants/valuationSource.js. Keep
// the three sources in lockstep with that file,
// tva-dashboard/src/constants/valuationSource.js and
// wl-customer/server/utils/valuationSource.ts. valuationDisplay.test.ts asserts
// the literal values so a rename on either side turns this suite red.

/** Where a property's estimatedValue came from — recorded per property. */
export type ValuationSource = "zoopla" | "rics" | "own";

/**
 * Every way the figure can be presented. A superset of ValuationSource: the
 * extra variant belongs to three legacy sites and is NOT a source (see below).
 */
export type ValuationDisplayKey = ValuationSource | "netCashflow";

export interface ValuationDisplay {
  key: ValuationDisplayKey;
  /** Caption rendered beside the figure. */
  label: string;
  /** Which property field the figure comes from. */
  valueField: "estimatedValue" | "netCashflow";
  /** Whether the property detail page's info card lists a valuation row. */
  showInInfoCard: boolean;
}

export const VALUATION_SOURCE_DEFAULT: ValuationSource = "zoopla";

export const VALUATION_SOURCES: readonly ValuationSource[] = [
  "zoopla",
  "rics",
  "own",
];

// netCashflow is a caption/number/visibility triple, not just a label: those
// sites show the monthly cashflow instead of the valuation and drop the detail
// row entirely. Keeping the three concerns in one descriptor is what stops a
// caption and a figure drifting apart.
export const VALUATION_DISPLAYS: Record<ValuationDisplayKey, ValuationDisplay> = {
  zoopla: {
    key: "zoopla",
    label: "Zoopla Valuation",
    valueField: "estimatedValue",
    showInInfoCard: true,
  },
  rics: {
    key: "rics",
    label: "RICS Valuation",
    valueField: "estimatedValue",
    showInInfoCard: true,
  },
  own: {
    key: "own",
    label: "Our Valuation",
    valueField: "estimatedValue",
    showInInfoCard: true,
  },
  netCashflow: {
    key: "netCashflow",
    label: "Net Monthly Cashflow",
    valueField: "netCashflow",
    showInInfoCard: false,
  },
};

// Three sites present the figure as a monthly cashflow, matched on theme NAME.
// This is a whole-site presentation rule, not a per-property fact, so it stays
// name-matched and deliberately outranks whatever a property records: those
// sites have no valuation to caption, and honouring a property's source there
// would swap the number shown for one the site does not use.
export const LEGACY_NET_CASHFLOW_THEME_NAMES: readonly string[] = [
  "properties.mypropertylisting.co.uk",
  "Test Site 1",
  "Test Site 2",
];

type ValuationThemeInput = Pick<ThemeProps, "name"> | null | undefined;
type ValuationPropertyInput =
  | { valuationSource?: ValuationSource | string | null }
  | null
  | undefined;

/**
 * Resolve how this listing should present its valuation.
 *
 * Precedence, highest first:
 *   1. legacy net-cashflow site, matched on theme name
 *   2. the source recorded on the property
 *   3. the Zoopla default
 *
 * Both arguments are optional: the theme store starts undefined and is often
 * unset on first paint, and every property that predates this feature has no
 * source. Either way the caller gets a usable descriptor.
 */
export function resolveValuationDisplay(
  theme?: ValuationThemeInput,
  property?: ValuationPropertyInput
): ValuationDisplay {
  if (theme?.name && LEGACY_NET_CASHFLOW_THEME_NAMES.includes(theme.name)) {
    return VALUATION_DISPLAYS.netCashflow;
  }

  const source = property?.valuationSource;
  if (source && VALUATION_SOURCES.includes(source as ValuationSource)) {
    return VALUATION_DISPLAYS[source as ValuationSource];
  }

  return VALUATION_DISPLAYS[VALUATION_SOURCE_DEFAULT];
}

/**
 * Pick the figure to render for a resolved display.
 *
 * Callers pass a normalised object because the two amounts are spelled
 * differently across the components that hold them (`netCashFlow` as a card
 * prop, `netCashflow` on the property document). Normalising at each call site
 * keeps the rounding rule in exactly one place.
 */
export function pickValuationAmount(
  display: ValuationDisplay,
  amounts: {
    estimatedValue?: number | string | null;
    netCashflow?: number | string | null;
  }
): number {
  if (display.valueField === "netCashflow") {
    return Math.ceil(Number(amounts.netCashflow ?? 0));
  }
  return Number(amounts.estimatedValue ?? 0);
}

/**
 * Whether there is a figure worth rendering, for surfaces that show "N/A"
 * instead of a number.
 *
 * The two fields treat zero differently, so this cannot be a truthiness check:
 *
 * - `estimatedValue: 0` means "no valuation recorded" — tva-api writes a literal
 *   0 when publishing a property that has none.
 * - a net monthly cashflow of 0 is a real, correct figure and must render as £0.
 */
export function hasValuationAmount(
  display: ValuationDisplay,
  amounts: {
    estimatedValue?: number | string | null;
    netCashflow?: number | string | null;
  }
): boolean {
  // Check the raw field first: pickValuationAmount normalises a missing amount
  // to 0, which for the cashflow field is indistinguishable from a real £0.
  const raw =
    display.valueField === "netCashflow"
      ? amounts.netCashflow
      : amounts.estimatedValue;
  if (raw === null || raw === undefined || raw === "") return false;

  const amount = pickValuationAmount(display, amounts);
  if (!Number.isFinite(amount)) return false;

  return display.valueField === "netCashflow" ? true : amount > 0;
}
